import {Link, useNavigate} from 'react-router-dom';
import {Navbar} from '@common/ui/navigation/navbar/navbar';
import {Footer} from '@common/ui/footer/footer';
import {Trans} from '@common/i18n/trans';
import {
  usePublicDocArticle,
  usePublicDocsCatalog,
} from '@app/docs/use-public-documentation';
import {FullPageLoader} from '@common/ui/progress/full-page-loader';
import clsx from 'clsx';
import {DescriptionIcon} from '@common/icons/material/Description';
import {FolderIcon} from '@common/icons/material/Folder';
import {groupDocsByCategory} from '@app/docs/group-docs-by-category';
import React, {useMemo, useCallback, useEffect} from 'react';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {useDocSlugFromUrl} from '@app/docs/use-doc-slug-from-url';
import {useHydratePublicDocsCache} from '@app/docs/use-hydrate-public-docs-cache';
import {extractDocHeadings} from '@app/docs/extract-doc-headings';
import {PublicDocsToc} from '@app/docs/public-docs-toc';
import {NotFoundPage} from '@common/ui/not-found-page/not-found-page';
import {getBootstrapData} from '@common/core/bootstrap-data/use-backend-bootstrap-data';
import {useAuth} from '@common/auth/use-auth';
import {UserCabinetNavbar} from '@app/dashboard/user-cabinet-navbar';
import './public-docs.css';

function docHref(slug: string): string {
  if (!slug || slug === 'index') {
    return '/docs';
  }
  return `/docs/${slug}`;
}

export function PublicDocsLayout() {
  useHydratePublicDocsCache();
  const {isLoggedIn} = useAuth();
  const navigate = useNavigate();
  const urlSlug = useDocSlugFromUrl();
  const catalogQuery = usePublicDocsCatalog();
  const articleQuery = usePublicDocArticle(urlSlug);

  const bootstrapArticles = getBootstrapData()?.loaders?.publicDocs?.articles;
  const unknownSlug =
    Boolean(urlSlug) &&
    bootstrapArticles != null &&
    !(urlSlug in bootstrapArticles);

  const groups = useMemo(() => {
    if (!catalogQuery.data) return [];
    return groupDocsByCategory(
      catalogQuery.data.files,
      catalogQuery.data.categories,
    );
  }, [catalogQuery.data]);

  const article = articleQuery.data;

  const tocHeadings = useMemo(
    () => extractDocHeadings(article?.html ?? ''),
    [article?.html],
  );

  useEffect(() => {
    if (!article?.slug) {
      return;
    }
    const hash = window.location.hash.slice(1);
    if (!hash) {
      return;
    }
    requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView();
    });
  }, [article?.slug]);

  const handleArticleClick = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const anchor = (e.target as HTMLElement).closest('a');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || !href.startsWith('/docs')) return;

      e.preventDefault();
      navigate(href.replace(/\.md$/i, ''));
    },
    [navigate],
  );

  if (
    unknownSlug ||
    (urlSlug && !articleQuery.isLoading && !articleQuery.data)
  ) {
    return <NotFoundPage />;
  }

  return (
    <div className="flex min-h-screen flex-col bg">
      <StaticPageTitle>
        {article?.title ?? <Trans message="Documentation" />}
      </StaticPageTitle>
      {isLoggedIn ? (
        <UserCabinetNavbar
          hideToggleButton
          className="sticky top-0 flex-shrink-0"
        />
      ) : (
        <Navbar
          menuPosition="homepage-navbar"
          className="sticky top-0 flex-shrink-0"
        />
      )}
      <div className="mx-auto flex w-full max-w-1408 flex-auto gap-32 px-16 py-32 md:px-24">
        <aside className="hidden w-280 flex-shrink-0 md:block">
          <p className="mb-16 text-sm font-semibold">
            <Trans message="Documentation" />
          </p>
          {catalogQuery.isLoading ? (
            <FullPageLoader />
          ) : (
            <nav className="flex flex-col gap-16">
              {groups.map(group => (
                <div key={group.path || '__root__'}>
                  <p className="mb-8 flex items-center gap-6 text-xs font-semibold uppercase tracking-wide text-muted">
                    {group.path ? <FolderIcon size="xs" /> : null}
                    {group.path ? group.title : <Trans message="General" />}
                  </p>
                  <ul className="flex flex-col gap-4">
                    {group.files.map(file => (
                      <li key={file.path}>
                        <Link
                          to={docHref(file.slug)}
                          className={clsx(
                            'flex items-center gap-8 rounded-panel px-10 py-8 text-sm hover:bg-hover',
                            article?.slug === file.slug &&
                              'bg-primary/10 font-medium text-primary',
                          )}
                        >
                          <DescriptionIcon size="sm" />
                          {file.title}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          )}
        </aside>
        <main className="min-w-0 flex-auto">
          {articleQuery.isLoading && !article ? (
            <FullPageLoader />
          ) : article ? (
            <article className="public-docs-article">
              <h1 className="mb-24 text-3xl font-bold">{article.title}</h1>
              <div
                className="md-html prose max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{__html: article.html}}
                onClick={handleArticleClick}
              />
            </article>
          ) : (
            <p className="text-muted">
              <Trans message="Article not found" />
            </p>
          )}
        </main>
        {tocHeadings.length > 0 && (
          <aside className="hidden w-240 flex-shrink-0 xl:block">
            <div className="sticky top-96">
              <PublicDocsToc headings={tocHeadings} />
            </div>
          </aside>
        )}
      </div>
      <Footer className="mx-14 md:mx-40" />
    </div>
  );
}
